import { useEffectEvent as useRACEffectEvent } from '@react-aria/utils';
import { createContext, type ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { useId } from 'react-aria';
import { useLocalizedMessages } from '../../../i18n/localization-provider.js';

/** Represents the type of row selection allowed within a table. */
export type RowSelectionMode = 'single' | 'multiple' | 'none';

export interface TableState {
  sortLabelId: string;
  rowSelectionMode: RowSelectionMode;
  areAllRowsSelected?: boolean;
  areSomeRowsSelected?: boolean;
  onAllRowsSelectionToggle?: (value?: boolean) => void;
  /** The selection state that will be applied to the next focused row. */
  pendingRowSelectionState?: boolean;
  setPendingRowSelectionState: (value: boolean | undefined) => void;
}

const TableStateContext = createContext<TableState | null>(null);

export function TableStateProvider({
  children,
  rowSelectionMode,
  areAllRowsSelected,
  areSomeRowsSelected,
  onAllRowsSelectionToggle,
}: {
  children: ReactNode;
  rowSelectionMode: RowSelectionMode;
  areAllRowsSelected?: boolean;
  areSomeRowsSelected?: boolean;
  onAllRowsSelectionToggle?: (value?: boolean) => void;
}) {
  // When the pending selection state is updated, we want to trigger a re-render,
  // but we also want immediate access to the new value.
  // Hence why it is stored as both a ref and a state.
  const pendingRowSelectionStateRef = useRef<boolean | undefined>(undefined);
  const [_pendingRowSelectionState, _setPendingRowSelectionState] = useState<boolean | undefined>(undefined);

  const sortLabelId = useId();
  const messages = useLocalizedMessages('table');

  const onAllRowsSelectionToggleMemoized = useRACEffectEvent(onAllRowsSelectionToggle);

  const tableState: TableState = useMemo(
    () => ({
      sortLabelId,
      rowSelectionMode,
      areAllRowsSelected,
      areSomeRowsSelected,
      onAllRowsSelectionToggle: onAllRowsSelectionToggleMemoized,
      get pendingRowSelectionState() {
        return pendingRowSelectionStateRef.current;
      },
      setPendingRowSelectionState(pendingRowSelectionState: boolean | undefined) {
        pendingRowSelectionStateRef.current = pendingRowSelectionState;
        _setPendingRowSelectionState(pendingRowSelectionState);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sortLabelId,
      rowSelectionMode,
      areAllRowsSelected,
      areSomeRowsSelected,
      onAllRowsSelectionToggleMemoized,
      _pendingRowSelectionState,
    ],
  );

  return (
    <TableStateContext.Provider value={tableState}>
      {children}
      <span style={{ display: 'none' }} id={sortLabelId}>
        {messages.format('toggleColumnSortOrder')}
      </span>
    </TableStateContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTableState(): TableState {
  const tableState = useContext(TableStateContext);
  if (!tableState) {
    throw new Error('Cannot use table state outside of a table');
  }

  return tableState;
}
